import { ArrowRight, ChevronRight } from 'lucide-react';
import { Fragment, useMemo } from 'react';
import { isValidCurlyBraces } from 'src/features/automations/[flowId]/components/utils/autoComplete';
import { NodeApprovalValue } from 'src/features/automations/[flowId]/components/utils/NodeDataTypes';
import JsonViewer from 'src/shared/components/typography/JsonViewer';
import { AccordionContent, AccordionItem, AccordionTrigger } from 'src/shared/components/ui/accordion';
import DynamicIcon, { Icons } from 'src/shared/components/ui/icons/dynamic-icon';
import { usePredictionParameters } from 'src/features/flows/queries/parameters/usePredictionParameters';
import { stripBraces } from 'src/features/flows/lib/general';
import { isArrayType, isPrimitiveType } from 'src/features/flows/lib/validators';
import { toTitleCase } from 'src/shared/lib/textFormat.utils';
import { ArrayType, CustomType, PrimitiveType } from 'src/features/flows/types/flow.types';
import { PredictionParameters, PropertyPrefixes } from 'src/features/flows/types/flowParameter.types';

interface ApprovalValueProps {
  id: string;
  label: string;
  type: PrimitiveType | ArrayType | CustomType;
  value: any;
  description?: string;
}

export default function ApprovalValue({ id, label, type, value, description }: ApprovalValueProps) {
  const { data: predictionParams } = usePredictionParameters();

  const predictionParamMap = useMemo(() => {
    return predictionParams?.reduce(
      (acc, param) => {
        acc[param.name] = param;
        return acc;
      },
      {} as Record<string, PredictionParameters>
    );
  }, [predictionParams]);

  const displayLabel = useMemo(() => {
    if (isValidCurlyBraces(label)) {
      const prefixValue = stripBraces(label).split('.');
      let displayLabel = prefixValue.slice(1).join('.');

      let prefix: NodeApprovalValue['prefix'] = prefixValue[0] as PropertyPrefixes;

      if (!['thread', 'facet'].includes(prefix)) {
        prefix = prefix === 'custom' ? 'custom' : undefined;
        displayLabel = stripBraces(label);
      }
      return predictionParamMap?.[label]?.displayName || displayLabel;
    }

    return label;
  }, [label, predictionParamMap]);

  const renderContent = (value: any, type: PrimitiveType | ArrayType | CustomType) => {
    if (value === undefined || value === null) {
      return (
        <div className="pl-4 flex items-center gap-1">
          <ArrowRight size={16} className="text-typography-grey shrink-0" /> —
        </div>
      );
    }

    if (type === 'custom') {
      if (typeof value === 'object') {
        return (
          <div className="pl-5 mt-1">
            <div className="w-full border bg-white rounded-sm px-2 max-h-[250px] overflow-auto">
              <JsonViewer>{value}</JsonViewer>
            </div>
          </div>
        );
      }

      return (
        <div className="pl-4 flex items-start gap-1 flex-wrap">
          <ArrowRight size={16} className="text-typography-grey shrink-0 mt-1" />
          <span className="whitespace-pre-wrap">{String(value).replace(/\\n/g, '\n')}</span>
        </div>
      );
    }

    if (type === 'custom[]') {
      if (Array.isArray(value) && value.length) {
        if (typeof value[0] === 'object') {
          return (
            <>
              {value.map((item: any, idx: number) => (
                <div key={idx} className="w-full border bg-white rounded-sm px-2 mt-1 first:mt-0">
                  <JsonViewer>{item}</JsonViewer>
                </div>
              ))}
            </>
          );
        }
        return (
          <div className="pl-4 flex items-center gap-1 flex-wrap flex-col gap-1">
            {value.map((item, index) => (
              <div key={index + '_' + item} className="flex items-start flex-wrap gap-1">
                <ArrowRight size={16} className="text-typography-grey shrink-0 mt-1" />
                <span className="whitespace-pre-wrap">{String(item).replace(/\\n/g, '\n')}</span>
              </div>
            ))}
          </div>
        );
      }
      return (
        <div className="pl-4 flex items-center gap-1">
          <ArrowRight size={16} className="text-typography-grey shrink-0" /> []
        </div>
      );
    }

    if (type.endsWith('[]')) {
      const primitiveType = type.replace('[]', '') as PrimitiveType;
      if (Array.isArray(value)) {
        return (
          <div className="pl-4 flex gap-1 flex-wrap flex-col gap-1">
            {value.map((item, index) => (
              <div key={index + '_' + item} className="flex items-start flex-wrap gap-1">
                <ArrowRight size={16} className="text-typography-grey shrink-0 mt-1" />
                <span className="whitespace-pre-wrap">{formatPrimitive(item, primitiveType)}</span>
              </div>
            ))}
          </div>
        );
      }
    }

    return (
      <div className="pl-4 flex items-start gap-1 flex-wrap">
        <ArrowRight size={16} className="text-typography-grey shrink-0 mt-1" />
        <span className="whitespace-pre-wrap">{formatPrimitive(value, type as PrimitiveType)}</span>
      </div>
    );
  };

  function formatDate(value: string | number | Date) {
    try {
      return new Date(value).toLocaleString();
    } catch {
      return String(value);
    }
  }

  function formatPrimitive(value: any, type: PrimitiveType) {
    switch (type) {
      case 'boolean':
        return String(!!value);
      case 'date':
        return formatDate(value);
      default:
        return String(value).replace(/\\n/g, '\n');
    }
  }

  const typeToIcon = (type: PrimitiveType | ArrayType | CustomType): Icons => {
    if (isPrimitiveType(type)) {
      if (type === 'date') {
        return 'Calendar';
      }

      if (type === 'boolean') {
        return 'ToggleRight';
      }

      if (type === 'email') {
        return 'AtSign';
      }

      if (type === 'number') {
        return 'Calculator';
      }

      return 'CaseSensitive';
    }

    if (isArrayType(type)) {
      return 'Brackets';
    }

    return 'Braces';
  };

  return (
    <AccordionItem key={id} value={id} className="border-none">
      <AccordionTrigger headless className="flex w-full items-center gap-1 group py-1">
        <ChevronRight
          size={16}
          className="text-typography-grey transition-transform duration-200 group-data-[state=open]:rotate-90 shrink-0"
        />
        <div className="flex flex-col gap-0.5 flex-1 min-w-0">
          <div className="rounded-sm whitespace-nowrap px-1.5 h-7 gap-1 flex-center font-normal bg-blue-50 text-primary truncate w-fit">
            <DynamicIcon name={typeToIcon(type)} size={12} className="shrink-0" />
            <div className="rounded-sm whitespace-nowrap px-1.5 h-5 gap-[1px] flex-center font-normal bg-blue-50 text-sm truncate w-fit">
              {displayLabel
                .split('.')
                .slice(-2)
                .map((part: string, index: number) => {
                  const isCustomValue = label.includes('custom');
                  const display = isCustomValue && index !== 0 ? part : toTitleCase(part);
                  return (
                    <Fragment key={index}>
                      {index !== 0 && <ChevronRight size={10} className="shrink-0 text-sm/50" />}
                      <span className="truncate text-ellipsis overflow-hidden">{display}</span>
                    </Fragment>
                  );
                })}
            </div>
          </div>
          {description && <p className="text-xs text-typography-grey truncate px-1.5 text-left">{description}</p>}
        </div>
      </AccordionTrigger>
      <AccordionContent>{renderContent(value, type)}</AccordionContent>
    </AccordionItem>
  );
}
